// Jeden dotaz na události za celou záložku. Okna si jen přihlásí kanály a reagují; nikdy nepollují sama.
// Spouštěče dotazu: časovač (vždy) a vnější podnět (později brána). Interval podle viditelnosti a požadavků oken.
import { api } from './api';
import type { EventItem, EventsResponse } from './contract';

type Handler = (events: EventItem[]) => void;

interface Subscription {
  channels: string[];
  handler: Handler;
  live: number | null;
  onReset: () => void;
}

const ACTIVE_MS = 2000;
const HIDDEN_MS = 45000;
const MAX_BACKOFF_MS = 30000;

export class EventBus {
  private subs = new Set<Subscription>();
  private cursor = 0;
  private timer: number | undefined;
  private running = false;
  private inFlight = false;
  private failures = 0;
  private wantImmediate = false;
  private online = true;
  onConnection: ((online: boolean) => void) | null = null;
  onLocked: ((locked: boolean) => void) | null = null;
  onSessionLost: (() => void) | null = null;

  start(cursor: number): void {
    this.cursor = cursor;
    this.running = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.schedule(ACTIVE_MS);
  }

  stop(): void {
    this.running = false;
    window.clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Okno si přihlásí kanály. Vrací odhlášení. */
  subscribe(channels: string[], handler: Handler, options: { live?: number | null; onReset?: () => void } = {}): () => void {
    const sub: Subscription = { channels, handler, live: options.live ?? null, onReset: options.onReset ?? (() => handler([])) };
    this.subs.add(sub);
    return () => {
      this.subs.delete(sub);
    };
  }

  /** Po vlastní akci: zeptat se hned, aby se vlastní okna obnovila okamžitě. */
  kick(): void {
    this.wantImmediate = true;
    this.schedule(50);
  }

  private onVisibility = (): void => {
    if (!document.hidden) this.kick();
  };

  private interval(): number {
    if (document.hidden) return HIDDEN_MS;
    let ms = ACTIVE_MS;
    for (const s of this.subs) if (s.live && s.live * 1000 < ms) ms = Math.max(500, s.live * 1000);
    if (this.failures > 0) {
      const backoff = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.failures, 5));
      ms = backoff + Math.random() * 1000;
    }
    return ms;
  }

  private schedule(ms: number): void {
    if (!this.running) return;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.poll(), ms);
  }

  private channelList(): string[] {
    const set = new Set<string>();
    for (const s of this.subs) for (const c of s.channels) set.add(c);
    return Array.from(set).slice(0, 200);
  }

  private async poll(): Promise<void> {
    if (!this.running || this.inFlight) {
      this.schedule(this.interval());
      return;
    }
    this.inFlight = true;
    this.wantImmediate = false;
    try {
      const res = await api<EventsResponse>('/events', undefined, { query: { since: String(this.cursor), channels: this.channelList().join(',') } });
      this.failures = 0;
      if (!this.online) {
        this.online = true;
        this.onConnection?.(true);
      }
      this.cursor = res.cursor;
      if (res.reset) for (const s of this.subs) s.onReset();
      else if (res.events.length) this.dispatch(res.events);
      this.onLocked?.(res.session.locked);
    } catch (e) {
      const status = (e as { status?: number }).status ?? 0;
      if (status === 401) {
        this.onSessionLost?.();
        this.inFlight = false;
        return; // po novém přihlášení se spustí znovu
      }
      this.failures++;
      if (this.online && this.failures >= 2) {
        this.online = false;
        this.onConnection?.(false);
      }
    } finally {
      this.inFlight = false;
    }
    this.schedule(this.wantImmediate ? 50 : this.interval());
  }

  private dispatch(events: EventItem[]): void {
    for (const s of this.subs) {
      const mine = events.filter((e) => s.channels.some((c) => e.ch === c || (!c.includes(':') && e.ch.startsWith(c + ':'))));
      if (mine.length) {
        try {
          s.handler(mine);
        } catch (err) {
          console.error('event handler', err);
        }
      }
    }
  }

  setCursor(cursor: number): void {
    this.cursor = cursor;
  }

  resume(): void {
    if (!this.running) {
      this.running = true;
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    this.failures = 0;
    this.kick();
  }
}

export const events = new EventBus();
